import { RequestStatus } from 'features/request/requestSlice';
import type { TUseRequestUpdaterProps } from 'features/request/useRequest';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { CatalogUerBaseCostDetailedData, CatalogUerBaseCostQuery } from 'gql/graphql';

import { useEffect, useMemo } from 'react';

import { catalogUERBaseCostGql } from '../createOrEditUerQueries';

export const useCatalogBaseCostData = ({
    catalogUniqueId,
    uerBaseCostId,
    withEffect = false,
    onSuccess,
}: {
    catalogUniqueId: string | undefined;
    uerBaseCostId: number | undefined;
    withEffect?: boolean;
    onSuccess?: (
        depsVars: TUseRequestUpdaterProps<
            CatalogUerBaseCostQuery,
            {
                catalogUniqueId: string;
                uerBaseCostId: number;
            }
        >,
    ) => void;
}) => {
    const variables = useMemo(() => {
        if (uerBaseCostId && catalogUniqueId) {
            return {
                catalogUniqueId,
                uerBaseCostId,
            };
        }

        return undefined;
    }, [catalogUniqueId, uerBaseCostId]);

    const [data, status, ,] = useRequest({
        requestId: 'catalogBaseCostData',
        query: catalogUERBaseCostGql,
        variables,
        withEffect: Boolean(variables && withEffect),
        dependenciesUpdater: onSuccess,
    });

    const isLoading = status === RequestStatus.FETCHING;

    const [reset] = useResetStoreData({
        requestId: 'catalogBaseCostData',
    });

    useEffect(
        () => () => {
            reset(undefined);
        },
        [reset],
    );

    return {
        data: { ...data?.catalogUERBaseCost?.data, ratios: [], ratioGroups: [] } as CatalogUerBaseCostDetailedData,
        isLoading,
    };
};
